(() => {
  const STORAGE_PREFIX = 'lfc_collection_return:';
  const LIST_NAME = document.querySelector('[data-lfc-collection]')?.dataset.collectionHandle || 'collection';

  const pushEvent = (eventName, item, extra = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ecommerce: {
        ...extra,
        items: [
          {
            item_id: item.productId,
            item_name: item.productTitle,
            price: Number(item.productPrice || 0),
            index: Number(item.listPosition || 0),
            item_list_name: LIST_NAME,
            ...(extra.quantity ? { quantity: Number(extra.quantity) } : {}),
          },
        ],
      },
    });
  };

  const productData = (element) => ({
    productId: element.dataset.productId,
    productTitle: element.dataset.productTitle,
    productPrice: element.dataset.productPrice,
    listPosition: element.dataset.listPosition,
  });

  const pageKey = () => `${STORAGE_PREFIX}${window.location.pathname}${window.location.search}`;

  const saveReturnState = (href) => {
    try {
      sessionStorage.setItem(
        pageKey(),
        JSON.stringify({
          href,
          scrollY: window.scrollY,
          savedAt: Date.now(),
        })
      );
    } catch (_error) {
      // Storage can be unavailable in private or embedded browsers.
    }
  };

  const restoreReturnState = () => {
    try {
      const raw = sessionStorage.getItem(pageKey());
      if (!raw) return;
      const state = JSON.parse(raw);
      if (!state || Date.now() - state.savedAt > 30 * 60 * 1000) {
        sessionStorage.removeItem(pageKey());
        return;
      }
      requestAnimationFrame(() => window.scrollTo({ top: Number(state.scrollY || 0), behavior: 'auto' }));
      sessionStorage.removeItem(pageKey());
    } catch (_error) {
      // Keep navigation functional when storage is blocked.
    }
  };

  const bindProductLinks = (scope = document) => {
    scope.querySelectorAll('[data-lfc-product-link]:not([data-lfc-bound])').forEach((link) => {
      link.dataset.lfcBound = 'true';
      link.addEventListener('click', () => {
        saveReturnState(link.href);
        pushEvent('select_item', productData(link), { item_list_name: LIST_NAME });
      });
    });
  };

  const bindQuickAdds = (scope = document) => {
    scope.querySelectorAll('[data-lfc-quick-add]:not([data-lfc-bound])').forEach((formHost) => {
      formHost.dataset.lfcBound = 'true';
      const form = formHost.querySelector('form');
      if (!form) return;
      form.addEventListener('submit', () => {
        const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
        pushEvent('add_to_cart', productData(formHost), { currency: 'BRL', quantity });
      });
    });
  };

  const announceList = (scope = document) => {
    const cards = [...scope.querySelectorAll('#product-grid [data-lfc-product-link]')];
    if (!cards.length) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'view_item_list',
      ecommerce: {
        item_list_name: LIST_NAME,
        items: cards.map((card) => ({
          item_id: card.dataset.productId,
          item_name: card.dataset.productTitle,
          price: Number(card.dataset.productPrice || 0),
          index: Number(card.dataset.listPosition || 0),
          item_list_name: LIST_NAME,
        })),
      },
    });
  };

  const bindLoadMore = (scope = document) => {
    const button = scope.querySelector('[data-lfc-load-more]:not([data-lfc-bound])');
    if (!button) return;
    button.dataset.lfcBound = 'true';
    button.addEventListener('click', async (event) => {
      event.preventDefault();
      if (button.getAttribute('aria-busy') === 'true') return;

      button.setAttribute('aria-busy', 'true');
      const originalText = button.textContent;
      button.textContent = 'Carregando…';

      try {
        const response = await fetch(button.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incomingGrid = documentFragment.querySelector('#product-grid');
        const currentGrid = document.querySelector('#product-grid');
        if (!incomingGrid || !currentGrid) throw new Error('Grade de produtos não encontrada');

        const newItems = [...incomingGrid.children];
        newItems.forEach((item) => currentGrid.appendChild(item));
        bindProductLinks(currentGrid);
        bindQuickAdds(currentGrid);

        const incomingPagination = documentFragment.querySelector('[data-lfc-pagination]');
        const currentPagination = document.querySelector('[data-lfc-pagination]');
        if (incomingPagination && currentPagination) {
          currentPagination.replaceWith(incomingPagination);
          bindLoadMore(document);
        } else if (currentPagination) {
          currentPagination.remove();
        }

        history.replaceState({}, '', button.href);
        const firstNewItem = newItems[0];
        if (firstNewItem) firstNewItem.scrollIntoView({ block: 'nearest' });
      } catch (_error) {
        window.location.assign(button.href);
      } finally {
        button.removeAttribute('aria-busy');
        button.textContent = originalText;
      }
    });
  };

  const initialize = (scope = document) => {
    bindProductLinks(scope);
    bindQuickAdds(scope);
    bindLoadMore(scope);
  };

  const bindFacetTracking = () => {
    document.querySelectorAll('#FacetFiltersForm, #FacetFiltersFormMobile, #FacetSortForm').forEach((form) => {
      if (form.dataset.lfcBound) return;
      form.dataset.lfcBound = 'true';
      form.addEventListener('change', (event) => {
        const field = event.target;
        if (!field?.name) return;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: field.name === 'sort_by' ? 'sort_item_list' : 'filter_item_list',
          item_list_name: LIST_NAME,
          filter_name: field.name,
          filter_value: field.value,
        });
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initialize();
    bindFacetTracking();
    restoreReturnState();
    announceList();

    if (document.querySelector('.collection--empty')) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'view_empty_item_list',
        item_list_name: LIST_NAME,
        location: window.location.href,
      });
    }

    const productGridContainer = document.querySelector('#ProductGridContainer');
    if (!productGridContainer) return;
    new MutationObserver(() => {
      initialize(productGridContainer);
      bindFacetTracking();
    }).observe(productGridContainer, {
      childList: true,
      subtree: true,
    });
  });
})();
